import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { useThemeColors } from '../hooks/useThemeColors';
import { useCanEdit } from '../components/EditOnly';
import { useAuthStore } from '../store/auth';
import {
  getMetrics, getModeration, refreshAccount, listAccounts,
  type FirebaseMetrics, type FirebaseTopRow, type FirebaseAccount,
} from '../api/firebase';
import { getOffers } from '../api/offers';
import { getTrackerStats } from '../api/tracker-stats';
import type { StatsRow } from '../api/tracker-types';
import { COUNTRY_NAME_TO_CODE, COUNTRY_MAP } from '../lib/countries';

interface CountryTrackerStats {
  clicks: number;
  regs: number;
  deps: number;
}

interface CountryModerationStats {
  total: number;
  blocked: number;
}

function KPI({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <div className="text-xs uppercase text-content-subtle">{label}</div>
      <div className="text-2xl font-semibold mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}

function TopList({
  title, rows, valueKey, offeredGeos, trackerByCode, moderationByCode,
}: {
  title: string;
  rows: FirebaseTopRow[];
  valueKey: 'users' | 'events';
  offeredGeos?: Set<string> | null;
  trackerByCode?: Map<string, CountryTrackerStats> | null;
  moderationByCode?: Map<string, CountryModerationStats> | null;
}) {
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);

  const pct = (n: number, d: number) => (d ? `${((n / d) * 100).toFixed(1)}%` : '—');

  const decorated = rows.map((r) => {
    const code = COUNTRY_NAME_TO_CODE[(r.name || '').toLowerCase()];
    const t = trackerByCode && code ? trackerByCode.get(code) : undefined;
    return { r, code, t };
  });

  // Sort by the metric (users / events) descending.
  decorated.sort((a, b) => b.r[valueKey] - a.r[valueKey]);

  const pageCount = Math.max(1, Math.ceil(decorated.length / PAGE_SIZE));
  // Clamp instead of resetting in an effect — rows can shrink on reload.
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = decorated.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Bar widths use the global max so they stay comparable across pages.
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <h3 className="font-semibold mb-3">{title}</h3>
      <div className="space-y-1">
        {pageRows.map(({ r, code, t }) => {
          const hasOffer = offeredGeos && code ? offeredGeos.has(code) : null;
          const mod = moderationByCode && code ? moderationByCode.get(code) : undefined;
          const blockedPct = mod && mod.total ? (mod.blocked / mod.total) * 100 : null;
          return (
            <div key={r.name} className="text-sm">
              <div className="flex justify-between items-center gap-2">
                <span className="truncate mr-2 flex items-center gap-1.5">
                  {code && <span aria-hidden>{COUNTRY_MAP[code]?.flag || ''}</span>}
                  <span className="truncate">{r.name || '(none)'}</span>
                  {offeredGeos && hasOffer === true && (
                    <span
                      title="Active offer available for this geo"
                      className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-success-soft text-success border border-success/30"
                    >
                      offer
                    </span>
                  )}
                  {offeredGeos && hasOffer === false && (
                    <span
                      title="No active offer for this geo"
                      className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-danger-soft text-danger border border-danger/30"
                    >
                      no offer
                    </span>
                  )}
                  {moderationByCode && blockedPct != null && (
                    <span
                      title={`${mod!.blocked.toLocaleString()} of ${mod!.total.toLocaleString()} devices blocked at moderation`}
                      className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                        blockedPct >= 50
                          ? 'bg-danger-soft text-danger border-danger/30'
                          : blockedPct >= 20
                            ? 'bg-warning-soft text-warning border-warning/30'
                            : 'bg-surface-muted text-content-muted border-border'
                      }`}
                    >
                      blocked {blockedPct.toFixed(0)}%
                    </span>
                  )}
                </span>
                <span className="text-content-muted">{r[valueKey].toLocaleString()}</span>
              </div>
              <div className="h-1 bg-surface-muted rounded mt-0.5">
                <div className="h-1 bg-blue-500 rounded" style={{ width: `${(r[valueKey] / max) * 100}%` }} />
              </div>
              {trackerByCode && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-content-subtle mt-0.5 tabular-nums">
                  {t ? (
                    <>
                      <span title="Tracker clicks">clk {t.clicks.toLocaleString()}</span>
                      <span title="Registrations">reg {t.regs.toLocaleString()}</span>
                      <span title="Deposits">dep {t.deps.toLocaleString()}</span>
                      <span title="click → reg (regs / clicks)">
                        click→reg <span className="text-content-muted font-medium">{pct(t.regs, t.clicks)}</span>
                      </span>
                      <span title="click → dep (deps / clicks)" className="text-content-muted">
                        click→dep <span className="font-semibold">{pct(t.deps, t.clicks)}</span>
                      </span>
                      <span title="reg → dep (deps / regs)">
                        reg→dep <span className="text-content-muted font-medium">{pct(t.deps, t.regs)}</span>
                      </span>
                    </>
                  ) : (
                    <span className="text-content-subtle">no tracker data</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-content-subtle text-sm">no data</div>}
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-sm">
          <button
            onClick={() => setPage(safePage - 1)}
            disabled={safePage === 0}
            className="px-2 py-1 rounded border border-border hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <span className="text-content-subtle tabular-nums">
            {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, decorated.length)} of {decorated.length}
          </span>
          <button
            onClick={() => setPage(safePage + 1)}
            disabled={safePage >= pageCount - 1}
            className="px-2 py-1 rounded border border-border hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function ProjectSwitcher({ accounts, currentId }: { accounts: FirebaseAccount[]; currentId: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const current = accounts.find((a) => a.id === currentId);

  // List not loaded yet (or current project missing) — show static name if we have it.
  if (accounts.length === 0 || !current) {
    return current ? <span className="font-medium">{current.name}</span> : null;
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1 rounded border border-border hover:bg-surface-muted text-sm font-medium"
      >
        <span className="truncate max-w-[16rem]">{current.name}</span>
        <span className="text-content-subtle text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 mt-1 z-30 min-w-[14rem] max-h-80 overflow-auto bg-surface border border-border rounded-lg shadow-lg py-1">
          {accounts.map((a) => (
            <Link
              key={a.id}
              to={`/firebase/${a.id}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-surface-muted ${
                a.id === currentId ? 'text-info font-medium' : 'text-content'
              }`}
            >
              <span className="w-3 text-info">{a.id === currentId ? '✓' : ''}</span>
              <span className="truncate">{a.name}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function FirebaseDetail() {
  const canEdit = useCanEdit();
  const admin = useAuthStore((s) => s.admin);
  const canManage = canEdit && (admin?.role === 'super_admin' || !!admin?.addons?.includes('developer'));
  const themeColors = useThemeColors();
  const { id } = useParams<{ id: string }>();
  const accountId = parseInt(id ?? '0', 10);
  const [range] = useState<7 | 30 | 90>(30);
  const [m, setM] = useState<FirebaseMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [offeredGeos, setOfferedGeos] = useState<Set<string> | null>(null);
  const [trackerByCode, setTrackerByCode] = useState<Map<string, CountryTrackerStats> | null>(null);
  const [moderationByCode, setModerationByCode] = useState<Map<string, CountryModerationStats> | null>(null);
  const [accounts, setAccounts] = useState<FirebaseAccount[]>([]);

  useEffect(() => {
    listAccounts().then((data) => setAccounts(data ?? [])).catch(() => setAccounts([]));
  }, []);

  const reload = () => {
    setLoading(true);
    getMetrics(accountId, range).then((data) => setM(data ?? null)).finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [accountId, range]);

  useEffect(() => {
    const to = new Date();
    const from = new Date(to.getTime() - range * 24 * 60 * 60 * 1000);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    getTrackerStats({
      from: `${fmt(from)} 00:00:00`,
      to: `${fmt(to)} 23:59:59`,
      group_by: 'country',
    })
      .then((rows: StatsRow[]) => {
        const m = new Map<string, CountryTrackerStats>();
        for (const r of rows) {
          if (!r.group_value) continue;
          m.set(r.group_value.toUpperCase(), {
            clicks: r.clicks, regs: r.regs, deps: r.deps,
          });
        }
        setTrackerByCode(m);
      })
      .catch(() => setTrackerByCode(new Map()));
  }, [range]);

  useEffect(() => {
    getModeration(accountId)
      .then((rows) => {
        const m = new Map<string, CountryModerationStats>();
        for (const r of rows) {
          if (!r.country) continue;
          m.set(r.country.toUpperCase(), { total: r.total, blocked: r.blocked });
        }
        setModerationByCode(m);
      })
      .catch(() => setModerationByCode(new Map()));
  }, [accountId]);

  useEffect(() => {
    getOffers()
      .then((offers) => {
        const s = new Set<string>();
        for (const o of offers) {
          if (o.status !== 'active' || !o.geo) continue;
          for (const code of o.geo.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean)) {
            s.add(code);
          }
        }
        setOfferedGeos(s);
      })
      .catch(() => setOfferedGeos(new Set()));
  }, []);

  if (loading || !m) return <div>Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link to="/firebase" className="text-info hover:underline">Back to Firebase</Link>
          <ProjectSwitcher accounts={accounts} currentId={accountId} />
        </div>
        {canManage && <button onClick={() => refreshAccount(accountId).then(() => setTimeout(reload, 1500))}
          className="bg-info hover:bg-info text-white px-3 py-1.5 rounded text-sm">
          Refresh now
        </button>}
      </div>

      {m.last_error && (
        <div className="mb-4 bg-danger-soft border border-danger/30 text-danger px-3 py-2 rounded text-sm">
          Last sync error: {m.last_error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="DAU" value={m.summary?.dau ?? 0} />
        <KPI label="WAU" value={m.summary?.wau ?? 0} />
        <KPI label="MAU" value={m.summary?.mau ?? 0} />
        <KPI label="Total Users (30d new)" value={m.summary?.total_users ?? 0} />
        <KPI
          label="Crash-free users (30d)"
          value={m.summary?.crash_free_rate_30d != null ? `${(m.summary.crash_free_rate_30d * 100).toFixed(2)}%` : '—'}
        />
        <KPI label="Crash-affected users (30d)" value={m.summary?.crash_affected_users_30d ?? 0} />
      </div>

      <div className="bg-surface border border-border rounded-lg p-4 mb-4">
        <h3 className="font-semibold mb-3">Daily users / registrations / deposits</h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={m.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke={themeColors.grid} />
              <XAxis dataKey="date" tick={{ fill: themeColors.axis }} stroke={themeColors.grid} />
              <YAxis tick={{ fill: themeColors.axis }} stroke={themeColors.grid} />
              <Tooltip
                contentStyle={{
                  backgroundColor: themeColors.tooltipBg,
                  border: `1px solid ${themeColors.tooltipBorder}`,
                  borderRadius: '8px',
                }}
                labelStyle={{ color: themeColors.tooltipLabel }}
              />
              <Legend />
              <Line type="monotone" dataKey="active_users" name="Active" stroke="#2563eb" />
              <Line type="monotone" dataKey="new_users" name="New" stroke="#16a34a" />
              <Line type="monotone" dataKey="regs" name="Regs" stroke="#f59e0b" />
              <Line type="monotone" dataKey="deps" name="Deps" stroke="#8b5cf6" />
              <Line type="monotone" dataKey="crash_affected_users" name="Crashed" stroke="#dc2626" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-lg p-4 mb-4">
        <h3 className="font-semibold mb-3">Retention (weekly cohorts)</h3>
        <table className="w-full text-sm">
          <thead className="bg-surface-muted">
            <tr><th className="text-left p-2">Cohort</th><th className="p-2">D1</th><th className="p-2">D7</th><th className="p-2">D30</th></tr>
          </thead>
          <tbody>
            {m.retention.map((r) => (
              <tr key={r.cohort_date} className="border-t border-border">
                <td className="p-2">{r.cohort_date}</td>
                <td className="p-2 text-center">{(r.d1 * 100).toFixed(1)}%</td>
                <td className="p-2 text-center">{(r.d7 * 100).toFixed(1)}%</td>
                <td className="p-2 text-center">{(r.d30 * 100).toFixed(1)}%</td>
              </tr>
            ))}
            {m.retention.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-content-subtle">no data</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TopList title="Top events" rows={m.top_events} valueKey="events" />
        <TopList title="Top countries" rows={m.top_countries} valueKey="users" offeredGeos={offeredGeos} trackerByCode={trackerByCode} moderationByCode={moderationByCode} />
        <TopList title="Top devices" rows={m.top_devices} valueKey="users" />
        <TopList title="App versions" rows={m.app_versions} valueKey="users" />
      </div>
    </div>
  );
}
